// Los tipos del guion, espejo de las estructuras de Go.
//
// Las claves son las MISMAS que el YAML porque el API las serializa con esos
// nombres. Que no diverjan es lo que permite que quien lee un YAML y quien usa
// la consola hablen del mismo campo.

export interface Paso {
  decir?: string
  capturar?: string
  /** Qué datos se pueden sacar de un mensaje libre, además del que captura.
   *  Solo declara qué buscar; quien lo saca es el extractor (modo B). */
  extraer?: Record<string, Campo>
  /** Salta el paso cuando el dato que captura ya está en la sesión. Es opt-in:
   *  hay pasos que capturan la misma clave para permitir reintentar. */
  omitir_si_conocido?: boolean
  opciones?: Record<string, string>
  si_no_entiende?: string
  invocar?: string
  argumentos?: Record<string, OrigenDato>
  ok?: string
  error?: Record<string, string>
  /** Ramas por el CONTENIDO de un resultado que salió bien, evaluadas antes de
   *  `ok`. Hay estados que el sistema del cliente reporta como éxito —«ya existe
   *  un compromiso vigente» es un dato de la consulta, no un error— y sin esto
   *  sólo se descubren fallando al escribir. La primera que se cumple gana. */
  cuando?: Condicion[]
  bloqueo?: Record<string, string>
  escalar?: string
  siguiente?: string
  fin?: boolean
  /** Entrega la conversación al agente hasta que la rama termine. Es el híbrido:
   *  tronco fijo con una rama conversacional. */
  delegar?: Delegacion
  /** Salta el paso cuando un hecho de la sesión ya se cumple —el consentimiento,
   *  que se pide una vez y vive fuera de la sesión—. */
  omitir_si?: string
}

export interface Delegacion {
  /** A dónde vuelve la conversación cuando el agente termina. */
  volver?: string
  /** Qué paso del guion atiende la rama cuando el modelo no puede. */
  respaldo?: string
  instrucciones?: string
}

/** Un mensaje de cualquiera de los dos lados de una conversación real. */
export interface TurnoConversacion {
  momento: string
  de: 'cliente' | 'bot'
  texto: string
  /** Sólo del bot: cómo salió y si el proveedor lo acusó. */
  estado?: string
  acuse?: string
  /** Sólo del cliente: adjunto, y cómo quedó su entrega en la cola. */
  media?: string
  procesado?: string
  /** Tipo del adjunto: un audio se escucha aquí mismo, lo demás se abre. */
  media_mime?: string
  /** Lo que decía la nota de voz, que es sobre lo que el bot decidió. */
  transcripcion?: string
}

export interface Condicion {
  /** Ruta dentro del resultado: `resultado.compromiso_vigente`. Sólo pregunta
   *  si el campo trae algo; no es un lenguaje de expresiones a propósito. */
  si: string
  /** Paso al que se salta cuando se cumple. */
  ir: string
}

export interface OrigenDato {
  desde?: string
  literal?: unknown
}

export interface ConfigFlujo {
  inicio: string
  pasos: Record<string, Paso>
}

export interface Modo {
  planner: 'menu' | 'flujo' | 'agente'
  renderer: 'plantilla' | 'llm'
  /** Modo A: el modelo interpreta lo que el menú no entendió. */
  interpretar?: boolean
  /** Modo B: el modelo llena varios datos de un mensaje libre. */
  extraer?: boolean
}

export interface Config {
  tenant?: string
  nombre?: string
  modo: Modo
  capacidades: string[]
  politicas?: Record<string, unknown>
  adapter?: Record<string, unknown>
  flujo?: ConfigFlujo
  plantillas?: Record<string, string>
  modelos?: Record<string, unknown>
}

export interface Borrador {
  tenant: string
  config: Config
  base_version?: number
  autor?: string
  nota?: string
  actualizado?: string
}

export interface FilaTenant {
  tenant: string
  nombre: string
  activo: boolean
  version_activa: number
  tiene_borrador: boolean
  planner?: string
}

export interface Campo {
  tipo?: string
  obligatorio?: boolean
  descripcion?: string
  [k: string]: unknown
}

export interface Capacidad {
  id: string
  nombre: string
  tipo: string
  descripcion?: string
  entrada?: Record<string, Campo>
  salida?: Record<string, unknown>
  resuelve?: string
  /** Mensajes de arranque por rol (ok, vacio, confirmar). Se COPIAN al tenant:
   *  desde ahí le pertenecen y cambiarlos aquí ya no lo toca. */
  plantillas_por_defecto?: Record<string, string>
}

export interface Usuario {
  id: number
  username: string
  nombre: string
  nit: string
  permisos: string[]
  /** Versión del binario que atiende. Se muestra en la barra superior: sin
   *  ella, "no me aparece X en la consola" es siempre la misma adivinanza
   *  entre código faltante y servidor sin actualizar. */
  version?: string
}

// Una version del guion, este publicada o no.
//
// La unidad es la VERSION y publicar es un estado suyo. Antes la pantalla
// listaba PUBLICACIONES, y eso dejaba invisible justo lo que hay que publicar:
// una version recien guardada no aparecia en ninguna parte.
export interface VersionGuardada {
  version: number
  autor?: string
  nota?: string
  creado: string
  vigente: boolean
  publicado?: string
  /** Mayor que uno = se revirtio a ella alguna vez. */
  publicaciones: number

  /** Con qué se compuso. Ausente = anterior a la 028, y eso no es un error:
   *  es «no se sabe», y la pantalla lo dice con palabras. */
  sello?: Sello
}

export interface Vigencia {
  Version: number
  Publicado: string
  Despublicado?: string | null
  Autor?: string
  Motivo?: string
}

export interface Traza {
  capacidad: string
  argumentos?: Record<string, unknown>
  politica: string
  error?: string
  /** El porqué del error: «el ERP respondió 409: COMPROMISO_VIGENTE_EXISTE».
   *  `error` es sólo la clase, y la clase no alcanza para arreglar nada. */
  detalle?: string
  duracion: string
  simulada?: boolean
}

/** Una línea de la traza de una conversación REAL.
 *
 *  Distinta de `Traza`, que es la de una prueba: aquélla nace de un turno que
 *  alguien acaba de simular y no tiene momento ni turno; ésta viene de la base y
 *  hay que poder ordenarla contra los mensajes. */
export interface TrazaReal {
  /** "2026-08-14 09:31:02". Es lo que permite intercalarla con los mensajes. */
  momento: string
  turno_id: string
  paso: number
  planner?: string
  capacidad?: string
  argumentos?: Record<string, unknown>
  politica?: string
  error?: string
  detalle?: string
  /** Por qué se cayó a un nivel inferior. Vacío = no degradó. */
  degradado?: string
  /** Lo que devolvió el ERP, sin mapear. Es lo más cargado de datos de la
   *  fila: va detrás de un clic, no a la vista. */
  respuesta?: string
  duracion_ms: number
  /** Qué modelo atendió y de qué proveedor. Estaban en la base desde hacía días
   *  y la consola no los pedía. */
  modelo?: string
  proveedor?: string
}

export interface TurnoProbado {
  mensaje: string
  respuesta: string[]
  escalado?: string
  error?: string
  trazas?: Traza[]
  duracion: string
  /** Lo que costó ESTE turno. Va turno a turno y no solo en el total porque así
   *  se ve cuál paso es el caro: casi siempre es uno. */
  costo_micros?: number
  modelo?: string
  /** Por qué se cayó a un nivel inferior: presupuesto, proveedor_caido, latencia. */
  degradado?: string
}

export interface ResultadoPrueba {
  turnos: TurnoProbado[]
  problemas: string[]
  aviso?: string
  costo_micros?: number
}

// ---------------------------------------------------------------------------
// Tipo de paso
// ---------------------------------------------------------------------------

// Un paso hace UNA de estas cosas. El editor pinta un formulario distinto para
// cada una, en vez de un formulario gigante con veinte campos donde diecisiete
// no aplican.
export type TipoPaso = 'mensaje' | 'pregunta' | 'menu' | 'capacidad' | 'escalar' | 'delegar' | 'final'

export function tipoDe(paso: Paso): TipoPaso {
  if (paso.invocar) return 'capacidad'
  if (paso.escalar) return 'escalar'
  // Antes de mirar si dice algo: un paso que delega no suele decir nada, y sin
  // esto se clasificaba como 'mensaje'. El editor lo pintaba como "Mensaje (no
  // envía nada)" y el selector de tipo, al tocarlo, dejaba la delegación viva
  // debajo de un formulario que no la muestra.
  if (paso.delegar) return 'delegar'
  if (paso.opciones && Object.keys(paso.opciones).length > 0) return 'menu'
  if (paso.capturar) return 'pregunta'
  if (paso.fin) return 'final'
  return 'mensaje'
}

export const ETIQUETAS: Record<TipoPaso, { titulo: string; icono: string; color: string }> = {
  mensaje:   { titulo: 'Mensaje',            icono: 'pi-comment',       color: 'secondary' },
  pregunta:  { titulo: 'Pregunta',           icono: 'pi-pencil',        color: 'info' },
  menu:      { titulo: 'Menú de opciones',   icono: 'pi-list',          color: 'info' },
  capacidad: { titulo: 'Consulta al sistema', icono: 'pi-bolt',         color: 'warn' },
  escalar:   { titulo: 'Pasar a un asesor',  icono: 'pi-user',          color: 'help' },
  delegar:   { titulo: 'Lo atiende el agente', icono: 'pi-sparkles',    color: 'warn' },
  final:     { titulo: 'Fin',                icono: 'pi-check-circle',  color: 'success' },
}

// Devuelve todos los pasos a los que salta este, con el nombre del campo. Lo usa
// el editor para pintar los destinos y el grafo para dibujar las aristas.
export function saltosDe(paso: Paso): Array<{ campo: string; destino: string }> {
  const out: Array<{ campo: string; destino: string }> = []
  if (paso.siguiente) out.push({ campo: 'siguiente', destino: paso.siguiente })
  if (paso.si_no_entiende) out.push({ campo: 'si no entiende', destino: paso.si_no_entiende })
  if (paso.ok) out.push({ campo: 'si sale bien', destino: paso.ok })
  for (const [k, v] of Object.entries(paso.opciones ?? {})) out.push({ campo: `opción "${k}"`, destino: v })
  for (const [k, v] of Object.entries(paso.error ?? {})) out.push({ campo: `error ${k}`, destino: v })
  for (const [k, v] of Object.entries(paso.bloqueo ?? {})) out.push({ campo: `bloqueo ${k}`, destino: v })
  // Las ramas por contenido del resultado y las dos salidas de una delegación
  // también son aristas. Faltaban, y el grafo de un guion híbrido salía mal:
  // pintaba en inalcanzable la rama entera del agente y todo lo que cuelga de su
  // respaldo, y marcaba el paso que delega como un callejón sin salida. El
  // backend arregló ese mismo falso positivo; aquí no había llegado.
  paso.cuando?.forEach((c, i) => {
    if (c.ir) out.push({ campo: `cuando ${i + 1}`, destino: c.ir })
  })
  if (paso.delegar?.volver) out.push({ campo: 'al volver del agente', destino: paso.delegar.volver })
  if (paso.delegar?.respaldo) out.push({ campo: 'respaldo sin modelo', destino: paso.delegar.respaldo })
  return out
}

// ---------------------------------------------------------------------------
// Evaluaciones
// ---------------------------------------------------------------------------
// Un caso NO afirma el texto de la respuesta. Comparar texto contra un modelo
// generativo falla siempre, y falla por motivos que no importan —una coma, un
// sinónimo— mientras deja pasar los que sí. Lo que se afirma son invariantes.

export interface Esperado {
  capacidades_invocadas?: string[]
  /** La afirmación que justifica el arnés: "con este mensaje el bot NO puede
   *  registrar un compromiso". Es una prueba de seguridad, no de calidad. */
  capacidades_prohibidas?: string[]
  debe_mencionar?: string[]
  no_debe_mencionar?: string[]
  termina_en?: string
  escala?: boolean
  max_turnos?: number
  sin_errores?: boolean
}

export interface Caso {
  id: string
  tenant: string
  nombre: string
  mensajes: string[]
  esperado: Esperado
  origen: 'manual' | 'produccion' | 'incidente'
  turno_id?: string
  activo: boolean
  autor?: string
  /** Fija lo que devuelve cada capacidad, por id. Es como se prueba una rama
   *  concreta sin esperar a que el ERP la produzca por casualidad. */
  respuestas?: Record<string, Record<string, unknown>>
}

export interface Intercambio {
  usuario: string
  bot: string[]
  capacidad?: string
  error?: string
}

export interface ResultadoCaso {
  caso: string
  nombre: string
  ok: boolean
  fallos?: string[]
  turnos: number
  costo_micros: number
  duracion: string
  conversacion?: Intercambio[]
  escrituras_interceptadas?: string[]
}

export interface Corrida {
  id: string
  tenant: string
  version?: number
  huella_prompt?: string
  momento: string
  resultados: ResultadoCaso[]
  costo_micros: number
  duracion: string
}

export interface ResumenCorrida {
  id: string
  version?: number
  huella_prompt?: string
  momento: string
  casos_ok: number
  casos_fallo: number
  costo_micros: number
  duracion_ms: number
  autor?: string
}

// ---------------------------------------------------------------------------
// Inteligencia
// ---------------------------------------------------------------------------
// Todo es opcional a propósito: ausente significa "hereda", y el valor cero es
// una decisión real. Un cliente que no configuró nada y otro que eligió "sin
// límite" tienen que poder distinguirse.

export interface Ajustes {
  /** "3s", "1500ms", "0" (sin límite). Nunca nanosegundos. */
  latencia_maxima?: string
  presupuesto_turno?: number
  presupuesto_conversacion?: number
  /** Tope de la conversación entera. Al llegar pasa a una persona. 0 = sin tope. */
  turnos_maximos?: number
  /** Turnos seguidos sin ejecutar nada: la señal de que el bot está girando. */
  turnos_sin_progreso?: number
  /** "2h", "45m", "0" (no caduca). Cuánto puede esperar alguien a un asesor
   *  antes de que la solicitud se cierre y se le avise. */
  espera_maxima_en_cola?: string
  /** Convierte las notas de voz en texto antes de que el bot las lea. */
  transcribir_audio?: boolean
  /** 'nunca' | 'espejo'. En espejo responde con nota de voz a quien le escribió
   *  en audio, acompañando al texto y nunca reemplazándolo. */
  responder_audio?: 'nunca' | 'espejo'
  enmascarar?: boolean
  modo_prompt?: 'servicio' | 'hibrido' | 'guiado'
  modo_cobro?: 'incluido' | 'traslado' | 'cupo'
  cupo_mensual?: number
  retencion_trazas_dias?: number
  perfil_planificar?: string
  /** A dónde cae el agente antes de rendirse con el modelo y pasar al guion. */
  perfil_economico?: string
  perfil_interpretar?: string
  perfil_extraer?: string

  /** Qué proveedor atiende lo que el ruteo no manda a otro sitio.
   *
   *  Vivía en una variable de entorno, así que cambiarlo exigía reiniciar y no
   *  aparecía en la cascada. No tiene valor de fábrica a propósito: sin que
   *  alguien elija, ningún cliente enciende IA. */
  llm_defecto?: string

  /** perfil → proveedores, EN ORDEN DE PREFERENCIA. El primero permitido gana.
   *
   *  Un perfil sin proveedores lo rechaza el servidor: una lista vacía no es
   *  "sin ruta", es una línea que alguien escribió creyendo que decía algo. */
  ruteo?: Record<string, string[]>

  /** Quién es este cliente: cómo se llama, por dónde lo contactan, qué dice de
   *  sí mismo. Va al prompt del modelo para que el bot pueda contestar preguntas
   *  sobre él.
   *
   *  NO lleva horarios a propósito: salen de `horario_atencion`, que es lo que
   *  decide de verdad si hay un asesor. Con dos fuentes el bot terminaría
   *  prometiendo una persona que no existe. */
  empresa?: Empresa

  /** A quién se le avisa cuando algo se rompe, y de qué. */
  alertas?: Alertas

  ia_habilitada?: boolean
}

/** La configuración de avisos por correo ante fallas graves.
 *
 *  Lo normal es declararla SOLO en el nivel global —quien opera la plataforma es
 *  quien atiende los incidentes— y que un cliente la declare únicamente cuando
 *  su propio equipo quiere enterarse de lo suyo. No se funden los niveles: un
 *  cliente que la declara declara la suya entera, sus tipos y su gente. */
export interface Alertas {
  /** Vacío = no se avisa nada. Es el estado de fábrica: mandarle correos a
   *  alguien que no los pidió no puede ser el comportamiento por defecto. */
  destinos?: string[]

  /** clave del catálogo → si se avisa. Una clave ausente hereda el valor de
   *  fábrica del catálogo, así que esto es un mapa y no una lista de encendidas:
   *  con una lista, «vacía» y «todas apagadas» se escribirían igual. */
  tipos?: Record<string, boolean>

  /** Cuántas ocurrencias y en cuánto tiempo, para las fallas que se miden por
   *  tasa. Las que no se recuperan solas avisan al primero y no usan esto. */
  umbral?: number
  ventana?: string

  /** Cuánto se calla una alerta ya avisada antes de repetirla. Es lo que decide
   *  si esto sigue sirviendo dentro de seis meses. */
  silencio?: string

  /** El tope duro, la última red: un incidente que toca a los treinta clientes
   *  a la vez produce treinta claves distintas y ninguna sofoca a la otra. */
  maximo_por_hora?: number
}

/** Una clase de falla que se puede avisar. Lo define el servidor: la consola
 *  pinta lo que él sabe detectar, no una lista propia que se desincroniza. */
export interface TipoAlerta {
  clave: string
  titulo: string
  detalle: string
  ambito: 'tenant' | 'plataforma'
  /** false = un solo caso ya es incidente porque no se recupera solo. */
  por_tasa: boolean
  de_fabrica: boolean
}

/** Un aviso que se emitió — o que no se pudo emitir, que es igual de importante:
 *  si el único rastro fuera el correo, un SMTP caído sería invisible. */
export interface RegistroAlerta {
  momento: string
  tenant: string
  tipo: string
  titulo: string
  detalle: string
  ocurrencias: number
  destinos: string
  /** enviada | fallida | sin_destinos | tope_por_hora */
  estado: string
  error_envio?: string
  resuelta?: string
}

/** Qué está fallando en un cliente, agrupado. Contesta «qué está roto» de un
 *  vistazo, que es distinto de «errores: 340» y de tener que abrir una
 *  conversación sabiendo el teléfono de antemano. */
export interface ErrorResumen {
  /** La CLASE pelada: no_disponible, timeout, no_autorizado… Las degradaciones
   *  llegan como `degradado:proveedor_caido`. */
  error: string
  capacidad?: string
  cuantas: number
  /** Última vez. Sin esto, un fallo ya resuelto y uno activo se ven igual. */
  ultimo: string
  /** Distingue «un caso que reintentó 40 veces» de «40 personas afectadas». */
  conversaciones: number
}

export interface ErrorTraza {
  momento: string
  error: string
  capacidad?: string
  /** El porqué legible: «el ERP respondió 409: COMPROMISO_VIGENTE». */
  detalle?: string
  degradado?: string
  /** Enmascarada: sólo los últimos cuatro dígitos. Así la guarda el sumidero. */
  conversacion?: string
  conversacion_id?: string
  argumentos?: Record<string, unknown>
  duracion_ms: number
}

export interface VistaErrores {
  resumen: ErrorResumen[]
  filas: ErrorTraza[]
  horas: number
  /** La lista se cortó. Lo dice el servidor: una pantalla que muestra 200 de
   *  4.000 sin avisar se lee como «sólo hubo 200». */
  recortado: boolean
}

export interface VistaAlertas {
  catalogo: TipoAlerta[]
  historial: RegistroAlerta[]
  /** Sin esto el vigilante detecta y registra, pero nadie se entera sin entrar
   *  a la consola. Tiene que verse antes de encender nada. */
  correo_configurado: boolean
}

export interface Empresa {
  nombre_legal?: string
  sector?: string
  sitio?: string
  /** Por dónde atiende, con el nombre que va a leer la persona. */
  canales?: Canal[]
  mision?: string
  vision?: string
  valores?: string[]
}

export interface Canal {
  nombre: string
  valor: string
}

export type CampoAjuste = keyof Ajustes
export type Nivel = 'fabrica' | 'plataforma' | 'tenant'

export interface Gasto {
  desde: string
  costo_micros: number
  turnos: number
  turnos_con_ia: number
  conversaciones: number
  ahorro_cache_pct: number
  latencia_p95_ms: number
  degradaciones: Record<string, number>
  errores: number
}

export interface Inteligencia {
  propios: Ajustes
  efectivos: Record<string, unknown>
  origen: Record<string, Nivel>
  plataforma: Ajustes
  fabrica: Ajustes
  gasto?: Gasto
}

// ---------------------------------------------------------------------------
// Credenciales de los proveedores de modelo
// ---------------------------------------------------------------------------
// No hay un campo con la clave, y no es un olvido: el API no la devuelve por
// ningún camino. Se registra y se reemplaza.
//
// La huella es un hash de seis caracteres. Responde "¿es la que rotamos el
// martes?" y "¿pruebas y producción tienen la misma?" sin revelar ninguna parte
// del valor.

export interface CredencialInfo {
  proveedor: string
  tenant?: string
  es_plataforma: boolean
  base_url?: string
  organizacion?: string
  activa: boolean
  cifrada: boolean
  huella?: string
  nota?: string
  autor?: string
  actualizado?: string
}

// ---------------------------------------------------------------------------
// MCP
// ---------------------------------------------------------------------------

export interface HerramientaExpuesta {
  nombre: string
  descripcion?: string
  escribe: boolean
  confirmacion: boolean
  /** Puede correr antes de que la persona esté identificada. */
  sin_identidad: boolean
}

export interface DescubrimientoMCP {
  protocolVersion: string
  transport: string
  endpoint: string
  publicado: boolean
  herramientas: HerramientaExpuesta[]
  aviso?: string
}

// ---------------------------------------------------------------------------
// Precios
// ---------------------------------------------------------------------------
// Micros por MILLÓN de tokens. Un micro es la millonésima de la moneda, así que
// 3.000.000 son 3 unidades por millón. En enteros porque el costo se suma
// millones de veces y en punto flotante el total del mes no cuadra con la suma
// de los días — que es justo la resta que alguien hace cuando la factura
// sorprende.

/** Un proveedor de modelos. Es una FILA de la tabla, administrable.
 *
 *  `implementacion` es el protocolo que habla, y eso sí vive en el código: hay
 *  un cliente HTTP escrito para Anthropic y otro para OpenAI. Una fila solo
 *  puede elegir entre los que existen; si cualquier texto valiera, alguien
 *  crearía "gemini", registraría una clave, la vería activa, y no funcionaría
 *  nada — sin un solo error. */
export interface ProveedorModelo {
  id: string
  implementacion: string
  nombre: string
  para_que: string
  /** perfil → identificador de modelo. Los perfiles son intenciones. */
  modelos: Record<string, string>
  activo: boolean
  nota?: string
  autor?: string
  actualizado?: string
}

/** Un protocolo que el binario sabe hablar. Viene del backend. */
export interface Implementacion {
  id: string
  /** Otros proveedores que hablan este mismo protocolo: para esos basta una
   *  fila y una credencial con su base_url, sin desplegar nada. */
  compatibles?: string[]
}

export interface Precio {
  proveedor: string
  modelo: string
  entrada: number
  salida: number
  cache_escribir: number
  cache_leer: number
  /** Micros por minuto de AUDIO. Solo los modelos de transcripción que cobran
   *  por duración; 0 para los que cobran por token. */
  por_minuto: number
  /** Micros por millón de caracteres sintetizados. Solo modelos de voz. */
  por_millon_caracteres: number
  moneda: string
  nota?: string
  autor?: string
  actualizado?: string
}

// ---------------------------------------------------------------------------
// La puerta para pasar a modo agente
// ---------------------------------------------------------------------------

export interface Requisito {
  nombre: string
  cumple: boolean
  actual: string
  exigido: string
  por_que: string
  bloquea: boolean
}

export interface Veredicto {
  listo: boolean
  requisitos: Requisito[]
  resumen: string
}

export interface AjustesPlataforma {
  vigentes: Ajustes
  fabrica: Ajustes
  historial: Array<{
    version: number
    publicado: string
    despublicado?: string
    autor?: string
    motivo?: string
  }>
}

/** Una clave del guion con su procedencia.
 *
 *  `capa` es el nombre de la vertical o integración de la que sale, o 'tenant'
 *  cuando la decide este cliente. `heredado` solo viene cuando es propia Y hay
 *  a dónde volver: enseñarlo es lo que hace que "volver a heredar" no sea un
 *  botón a ciegas. */
export interface ClaveDelGuion {
  clave: string
  capa: string
  propia: boolean
  /** Texto plano, ya descodificado: el servidor guarda un fragmento YAML pero
   *  lo que llega aquí es lo que una persona escribió. Sin eso, un 💳 se vería
   *  como "💳". */
  valor: string
  heredado?: string
  /** Falso cuando el valor no es texto —una lista, un mapa, un número—: no se
   *  ofrece en un cuadro de escribir. */
  texto: boolean
  autor?: string
}

export interface CapaCompartida {
  nombre: string
  commit: string
  actualizada: string
  bytes: number

  /** Versión que rige hoy. Cero = existe pero nadie la publicó, que es lo que
   *  pasa cuando el despliegue trae una vertical y se queda esperando. */
  vigente: number
}

export interface RespuestaCapas {
  bases: string[]
  capas: CapaCompartida[]
  claves: ClaveDelGuion[]
}

// ---------------------------------------------------------------------------
// Versiones en la base: el catálogo, las capas compartidas y el sello
// ---------------------------------------------------------------------------
// Lo que convierte «todo en la base» en «un solo punto de versiones» no es
// tener tablas: es que la versión publicada de un cliente diga DE QUÉ ESTÁ
// HECHA. Eso es el sello, y es lo que permite que publicar un catálogo nuevo
// no le mueva el guion a nadie.

export interface VersionCatalogo {
  version: number
  sha256: string
  autor?: string
  nota?: string
  creado: string
  bytes: number
  vigente: boolean
}

export interface VersionCapa {
  nombre: string
  version: number
  sha256: string
  autor?: string
  nota?: string

  /** Por dónde entró. 'despliegue' significa que alguien la revisó en un PR y
   *  'consola' que alguien la escribió en caliente: no se revisan igual. */
  origen: 'consola' | 'empujon' | 'despliegue' | 'importada'
  commit?: string
  creado: string
  bytes: number
  vigente: boolean
}

/** Una capa con versión más nueva que la vigente. El despliegue guarda y no
 *  publica, así que esto es lo normal después de un merge — no un fallo. */
export interface CapaPendiente {
  nombre: string
  vigente: number
  ultima: number
}

/** Un cliente que hereda de una capa, o que declara una capacidad, con la
 *  versión a la que está sellado. Es el radio de daño. */
export interface HeredaDe {
  tenant: string
  version: number

  /** Cero = sin sello, anterior a la 028. No es un error: es «no se sabe». */
  sellada: number
}

export interface Sello {
  catalogo?: number
  bases?: Record<string, number>
}

export interface Adopcion {
  tenant: string
  version: number
  sello: Sello
  avisos?: string[]
}
